import {useCallback, useState} from 'react';

import {Box, Skeleton, Typography, useMediaQuery} from '@mui/material';
import i18n, {t} from 'i18next';

import ChevronRight from 'assets/icons/chevronRight';
import theme from 'config/theme';
import useAnalytics, {BASKET_PDP_ATTRIBUTES, SUGGESTEDLIST_CHANNELS, EVENT_SOURCE} from 'contexts/analytics-context';
import {useAppDispatch, useAppSelector} from 'hooks';
import {redeemEcode} from 'services/loyalty/loyalty-service';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {getBasketCalculationAction} from 'store/actions/basket-action';
import {
  ECODE_REDEEM_STATUS,
  TrackEventType,
  LoyaltySource,
  LoyaltyEvent,
  LoyaltyGraphQLErrorWithExtensions
} from 'types';
import {filter1PItems, filter3PItems} from 'utils/helper';

import {RedeemPromotionBtn, RedeemPromotionInput, StyledProPointsSuggestionContainer} from './styles';

const ActivateEcode = () => {
  const analytics = useAnalytics();

  const dispatch = useAppDispatch();
  const {defaultMembershipCard} = useAppSelector((state) => state?.app);
  const isMobileView = useMediaQuery(theme.breakpoints.only('mobile'));
  const isReactNativeView = ReactNative.isReactNativeWebView();
  const {basket} = useAppSelector((state) => state?.basket);

  const [eCode, setECode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const activeEcodeAnalytics = useCallback(
    (status: ECODE_REDEEM_STATUS, eventType: TrackEventType) => {
      analytics.trackEvent(LoyaltyEvent.REWARDS_CENTER_ACTIVATE, {
        status,
        eventType,
        pageName: BASKET_PDP_ATTRIBUTES.PAGE_NAME,
        sourceEvent: LoyaltySource.BASKET_PRO_POINTS_PROMOTIONS,
        makroCard: defaultMembershipCard?.cardNumber ?? '',
        storeId: basket?.storeCode || '',
        channel: SUGGESTEDLIST_CHANNELS.MAKRO_PRO,
        segment_source_name: isReactNativeView ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
        basketSize: basket?.basketItems?.length || 0,
        onePCount: filter1PItems(basket?.basketItems || []).length || 0,
        threePCount: filter3PItems(basket?.basketItems || []).length || 0
      });
    },
    [analytics, defaultMembershipCard?.cardNumber, basket?.storeCode, isReactNativeView, basket?.basketItems]
  );

  const handleRedeemCode = async () => {
    if (eCode.length) {
      setIsLoading(true);
      activeEcodeAnalytics(ECODE_REDEEM_STATUS.ACTIVATE, TrackEventType.CLICK);
      try {
        await redeemEcode(eCode);
        SendBrowserEvent.showActivateEcodeSuccessToast();
        activeEcodeAnalytics(ECODE_REDEEM_STATUS.SUCCESS, TrackEventType.VIEW);
        dispatch(getBasketCalculationAction());
      } catch (error: unknown) {
        const locale = i18n.language === 'th' ? 'th' : 'en';

        let err: LoyaltyGraphQLErrorWithExtensions | undefined;

        if (Array.isArray(error)) {
          err = error[0] as LoyaltyGraphQLErrorWithExtensions;
        } else {
          err = error as LoyaltyGraphQLErrorWithExtensions;
        }
        let errorMessage = t('message.somethingWentWrong');
        if (err) {
          errorMessage = err?.extensions?.error?.[locale] ?? '';
        }

        SendBrowserEvent.showErrorToast(undefined, errorMessage);
        activeEcodeAnalytics(ECODE_REDEEM_STATUS.FAILED, TrackEventType.VIEW);
      }

      setECode('');
      setIsLoading(false);
    }
  };

  const openHowToGetEcodeModal = useCallback(() => {
    SendBrowserEvent.openHowToGetEcodeModal();
  }, []);

  if (!defaultMembershipCard?.cardNumber) {
    return null;
  }

  return (
    <StyledProPointsSuggestionContainer isReactNativeView={isReactNativeView || isMobileView} bgcolor="white">
      <Box display="flex" alignItems="center" gap="4px" justifyContent="space-between">
        <Typography fontWeight={600} lineHeight="24px" color="#212124">
          {t('proPointPromotion.redeemPromotion')}
        </Typography>
      </Box>
      <Box
        mt="4px"
        height="56px"
        display="flex"
        alignItems="center"
        gap="4px"
        justifyContent="space-between"
        border="1px solid #C7C7C7"
        borderRadius="4px"
        padding="8px 16px"
        boxSizing="border-box"
      >
        <RedeemPromotionInput
          name="eCode"
          value={eCode}
          size="small"
          variant="standard"
          onChange={(e) => setECode(e.target.value)}
          placeholder={t('proPointPromotion.enterCode')}
        />

        <RedeemPromotionBtn disabled={!eCode?.length || isLoading} onClick={handleRedeemCode}>
          {isLoading ? (
            <Skeleton animation="wave" variant="rounded" width="auto" height={24} />
          ) : (
            t('proPointPromotion.redeem')
          )}
        </RedeemPromotionBtn>
      </Box>
      <Box display="flex" mt="6px" alignItems="center" onClick={openHowToGetEcodeModal}>
        <Typography fontWeight={600} fontSize="12px" lineHeight="20px" color="#E82026">
          {t('proPointPromotion.howToGetCode')}
        </Typography>
        <ChevronRight fillColor="#E82026" width={12} height={12} />
      </Box>
    </StyledProPointsSuggestionContainer>
  );
};

export default ActivateEcode;
